import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Select, AutoComplete } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'

import {
  ErrorContainer,
  FormInput,
  FormRules,
  FormItem,
  WarningMessage
} from 'portal/components/RecordEditing'
import { FormInfraction } from 'portal/components/RecordEditing/FormInfraction'

import { EWC_CODES } from 'portal/lib/constants'
import {
  CheckQuantity,
  CheckVolume,
  CheckWasteDescription,
  CheckWeight
} from 'portal/lib/functions/SimpleDataValidation'
import { Spacer } from 'portal/lib/primitives'

import { useCountryDrivenValues } from 'portal/lib/hooks'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'

export function Waste({
  isImperial = false,
  lineItemDataSourceTracking = {},
  form
}) {
  const [initialContainerType, setInitialContainerType] = useState('')
  const [containerTypeWarning, setContainerTypeWarning] = useState(null)

  const { wasteFields } = useCountryDrivenValues()
  const { lineItem } = useParams()
  const getInfraction = useInfractionGetter()

  const containerTypeInfraction = getInfraction(
    `data.items[${lineItem}].containerType`,
    {
      isPrefix: true
    }
  )
  const wasteCodeInfraction = getInfraction(
    `data.items[${lineItem}].wasteCode`,
    {
      isPrefix: true
    }
  )

  useEffect(() => {
    form.register('knownContainerType')
    form.register('wasteCode')
    form.register('volumeEstimated')
    form.register('weightEstimated')
  }, [form])

  const { trigger } = form

  useEffect(() => {
    // Trigger validation for all fields on load
    trigger()
  }, [trigger])

  const { containerTypes: allContainerTypes } = useSelector(
    state => state.containerTypes
  )

  const containerTypesOptions = allContainerTypes
    .filter(ct => {
      if (typeof ct === 'string') {
        return ct !== 'Unknown'
      }
      return ct.name !== 'Unknown'
    })
    .map(ct => {
      if (typeof ct === 'string') {
        return {
          label: ct,
          value: ct,
          key: ct
        }
      }
      // Handle object with name and optional volumeM3
      const name = ct.name
      const label = ct.volumeM3 ? (
        isImperial ? (
          <>
            {name} ({(ct.volumeM3 * 1.30795).toFixed(2)} cu yd)
          </>
        ) : (
          <>
            {name} ({ct.volumeM3} m<sup>3</sup>)
          </>
        )
      ) : (
        name
      )
      return {
        label,
        value: name,
        key: name
      }
    })

  // Work around the BugFix (below) issue where initial render does not have form values
  useEffect(() => {
    if (initialContainerType || Object.keys(form.getValues()).length === 0) {
      return
    }

    const initialValue = form.getValues('knownContainerType')
    if (initialValue) {
      setInitialContainerType(initialValue)
    }
  }, [form, initialContainerType, setInitialContainerType])

  // Not watching KnownContainerType because it causes re-render and updates AutoCompleted, meaning user changes immediately are ignored
  const wasteCode = form.watch('wasteCode')
  const volumeEstimated = form.watch('volumeEstimated')
  const weightEstimated = form.watch('weightEstimated')

  const ewcCodeOptions = EWC_CODES.map(code => ({
    label: `${code.ewcCode} - ${code.description}`,
    value: code.ewcCode,
    key: code.ewcCode
  }))

  const setWasteCode = value => {
    form.setValue('wasteCode', value ? value : '', {
      shouldDirty: true
    })
  }

  const setKnownContainerType = value => {
    form.setValue('knownContainerType', value, {
      shouldDirty: true
    })

    if (showWarningIfUnilistedContainerType(value)) {
      return
    }

    showWarningIfContainerTypeHasChanged(value)
  }

  const showWarningIfUnilistedContainerType = useCallback(
    value => {
      if (value !== null && value !== '') {
        const isListed = allContainerTypes.some(ct => {
          if (typeof ct === 'string') {
            return ct === value
          }
          return ct.name === value
        })

        if (!isListed) {
          setContainerTypeWarning(
            'Warning: Using an unlisted Container Type will remove weight/volume if ticket data is missing.'
          )
          return true
        }
      }

      return false
    },
    [allContainerTypes]
  )

  const showWarningIfContainerTypeHasChanged = useCallback(
    value => {
      if (initialContainerType === value) {
        setContainerTypeWarning(null)
      } else if (value === '' || value === null) {
        setContainerTypeWarning(
          'Warning: Removing Container Type will remove weight/volume if ticket data is missing.'
        )
      } else {
        setContainerTypeWarning(
          'Warning: Updating Container Type may change weight/volume if ticket data is missing.'
        )
      }
    },
    [initialContainerType]
  )

  const VolumeLabelBody = !isImperial ? (
    <>
      Volume (m<sup>3</sup>)
    </>
  ) : (
    'Volume'
  )

  const WeightLabelBody = !isImperial ? 'Weight (t)' : 'Weight'

  // BugFix: Sometime the form does not get initialised with the default values
  // so we trigger validation to force it to load the default values
  if (Object.keys(form.getValues()).length === 0) {
    form.trigger()
    return null
  }

  return (
    <>
      <FormInput.Hidden name="id" />
      <FormItem
        label="Container type"
        valueAdd={{
          fieldName: 'knownContainerType',
          ...lineItemDataSourceTracking?.knownContainerType
        }}
        infractionAlias={`data.items[${lineItem}].containerType`}
      >
        <AutoComplete
          name="knownContainerType"
          autoFocus
          allowClear
          defaultValue={
            form.getValues('knownContainerType') ?? initialContainerType
          }
          options={containerTypesOptions}
          placeholder="Enter or select Container Type"
          filterOption={(inputValue, option) =>
            option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
          }
          onClear={() => {
            setKnownContainerType('')
          }}
          onChange={value => {
            setKnownContainerType(value)
          }}
          suffixIcon={<Icons.SearchOutlined />}
        />

        {containerTypeWarning && (
          <WarningMessage>{containerTypeWarning}</WarningMessage>
        )}
        {containerTypeInfraction && containerTypeInfraction[0] && (
          <ErrorContainer>
            <FormInfraction infraction={containerTypeInfraction[0]} />
          </ErrorContainer>
        )}
      </FormItem>

      {wasteFields && wasteFields.wasteDescription && (
        <FormItem
          label="Waste description"
          valueAdd={{
            fieldName: 'wasteDescription',
            ...lineItemDataSourceTracking?.wasteDescription
          }}
        >
          <FormInput.Text
            label="Waste description"
            name="wasteDescription"
            simpleDataValidationRules={CheckWasteDescription}
            infractionAlias={`data.items[${lineItem}].wasteDescription`}
          />
        </FormItem>
      )}

      {wasteFields && wasteFields.wasteCode && (
        <FormItem
          label="Waste code (EWC)"
          valueAdd={{
            fieldName: 'wasteCode',
            ...lineItemDataSourceTracking?.wasteCode
          }}
        >
          <Select
            name="wasteCode"
            allowClear
            showSearch
            options={ewcCodeOptions}
            value={wasteCode}
            placeholder="Enter or select EWC code"
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
            onChange={value => {
              setWasteCode(value)
            }}
          />
          {wasteCodeInfraction && wasteCodeInfraction[0] && (
            <ErrorContainer>
              <FormInfraction infraction={wasteCodeInfraction[0]} />
            </ErrorContainer>
          )}
        </FormItem>
      )}
      <Spacer.Small />
      {wasteFields && wasteFields.volume && (
        <FormItem
          label={VolumeLabelBody}
          valueAdd={{
            fieldName: 'volume',
            ...lineItemDataSourceTracking?.volume
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Volume"
              name="volume"
              type="number"
              placeholder={volumeEstimated ? `est. ${volumeEstimated}` : ''}
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              simpleDataValidationRules={v =>
                CheckVolume(v, isImperial ? 'CubicYard' : 'CubicMeter')
              }
              inputUnit={isImperial ? 'cubic yard' : null}
              infractionAlias={`data.items[${lineItem}].volume`}
            />
          </MultiInputWrapper>
        </FormItem>
      )}

      {wasteFields && wasteFields.weight && (
        <FormItem
          label={WeightLabelBody}
          valueAdd={{
            fieldName: 'weight',
            ...lineItemDataSourceTracking?.weight
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Weight"
              name="weight"
              type="number"
              placeholder={weightEstimated ? `est. ${weightEstimated}` : ''}
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              simpleDataValidationRules={v =>
                CheckWeight(v, isImperial ? 'Pound' : 'Tonne')
              }
              inputUnit={isImperial ? 'lbs' : null}
              infractionAlias={`data.items[${lineItem}].weight`}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      <Spacer.Small />
      {wasteFields && wasteFields.quantity && (
        <FormItem
          label="Quantity"
          valueAdd={{
            fieldName: 'quantity',
            ...lineItemDataSourceTracking?.quantity
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Quantity"
              name="quantity"
              type="number"
              defaultValue="1"
              rules={FormRules.PositiveDecimal()}
              simpleDataValidationRules={CheckQuantity}
              infractionAlias={`data.items[${lineItem}].quantity`}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
    </>
  )
}

export const MultiInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-width: 15rem;
  gap: 0 0.5rem;
  flex: 0 1 auto;
`
